// src/shared/bug-reporter/hooks/useAutoCapture.ts
// Resolves the non-screenshot pieces of the auto-capture payload: route,
// viewport, browser/OS/device. Pure helpers — no side effects, no async.

import { useMemo } from 'react'
import { UAParser } from 'ua-parser-js'
// [admin-sync] Canonical imports useLocation from 'react-router-dom'. Admin runs
// React Router v7 (framework mode) which re-exports it from 'react-router'. See SYNC.md.
import { useLocation } from 'react-router'
import type { UserAgentInfo } from '../types'

export function detectUserAgent(uaString?: string): UserAgentInfo {
  const parser = new UAParser(uaString ?? (typeof navigator !== 'undefined' ? navigator.userAgent : ''))
  const result = parser.getResult()
  const browser = [result.browser.name, result.browser.version].filter(Boolean).join('/')
  const os = [result.os.name, result.os.version].filter(Boolean).join(' ')
  const device = [result.device.vendor, result.device.model].filter(Boolean).join(' ').trim()
  return {
    browser: browser || 'Unknown',
    os: os || 'Unknown',
    device: device || (result.device.type ?? 'Desktop'),
  }
}

export function currentViewport(): string {
  if (typeof window === 'undefined') return '0x0'
  return `${window.innerWidth}x${window.innerHeight}`
}

/**
 * Pulls the live signals the modal renders in its auto-capture list. The
 * screenshot is *not* fetched here — see useScreenshotCapture.
 */
export function useAutoCaptureBase() {
  const location = useLocation()
  const route = `${location.pathname}${location.search}`
  const ua = useMemo(() => detectUserAgent(), [])
  // Viewport recomputes only on remount; this hook is called inside the modal
  // so a fresh value is read each time the modal opens.
  const viewport = currentViewport()
  return { route, ua, viewport }
}
